import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import type { ITakeoffMaterial } from '@interfaces/ICadTakeoff';
import type { IClaimElements } from '@interfaces/IScheduler';
import { canMeasure, chosenIds, claimQuantity, floorBands, type ClaimUnitKey, type IFloorBand } from '../../stock/claimElements';
import styles from './TaskPanelStockTab.module.css';

interface IStockClaimElementsProps {
  /** The claim material's take-off rows, already grouped to one per element
   *  type by `useTaskStock.rowsFor` — one row here per row there. */
  rows: ITakeoffMaterial[];
  elements: IClaimElements[];
  /** The elements other tasks hold on the same material, by globalId → task
   *  name — they come off each type's free count. */
  taken: ReadonlyMap<string, string>;
  /** The claim's unit as the CAD understands it, null when the CAD cannot
   *  measure it at all (kg, bags…) — then every row is inert. */
  unitKey: ClaimUnitKey | null;
  /** The rendered unit label, for the subtotals. */
  unitLabel: string;
  /** The claim's quantity — the sum of the subtotals, shown as the footer. */
  total: number;
  /** The task's floors in order, their names, and which floor an element
   *  sits on (`useTaskStock.floorOf`) — the per-floor split under each type
   *  when the task spans several. */
  floorIds: string[];
  floorNames: string[];
  floorOf: (globalId: string) => string | null;
  /** Opens the element list for one type. */
  onSelect: (kind: string) => void;
}

/** "{floor} · {n}" per floor holding elements of the type, in floor order —
 *  "{floor} · {chosen} of {n}" in navy where some are chosen there. */
function FloorSplit({ bands, chosen }: { bands: IFloorBand[]; chosen: ReadonlySet<string> }) {
  const { t } = useTranslation();
  return (
    <span className={styles.typeSplit}>
      {bands.map((band, i) => {
        const count = band.elements.length;
        const picked = band.elements.filter((el) => chosen.has(el.globalId)).length;
        return (
          <Fragment key={band.floorId}>
            {i > 0 && ' · '}
            {picked > 0 ? (
              <span className={styles.typeSplitChosen}>
                {t('scheduler.panel.stock.floorSplitChosen', { floor: band.floorName, chosen: picked, count })}
              </span>
            ) : (
              <span>{t('scheduler.panel.stock.floorSplit', { floor: band.floorName, count })}</span>
            )}
          </Fragment>
        );
      })}
    </span>
  );
}

/**
 * The types block: one row per CAD element type saying how many of its
 * elements the claim covers out of how many are free, a *Select…* link into
 * the element list, and the sum of the chosen elements' own measures on the
 * right — and the summed quantity as a footer. This is the claim's ONLY
 * input for a CAD material; the Quantity field below merely displays what it
 * comes to. A task on several floors gets, under each type, where its
 * elements are, so the user sees the floors before picking.
 *
 * A type the CAD cannot measure in the chosen unit is shown but inert: its
 * elements would contribute an unknown amount, so there is nothing to select.
 */
export function StockClaimElements({
  rows,
  elements,
  taken,
  unitKey,
  unitLabel,
  total,
  floorIds,
  floorNames,
  floorOf,
  onSelect,
}: IStockClaimElementsProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.types}>
      <div className={styles.typesHead}>
        <span>{t('scheduler.panel.stock.elements')}</span>
        <span>{unitLabel}</span>
      </div>

      {rows.map((row) => {
        const ids = chosenIds(elements, row.kind);
        const takenCount = row.elements.filter((el) => taken.has(el.globalId)).length;
        const free = row.elementCount - takenCount;
        const measurable = canMeasure(row, unitKey);
        // A throwaway entry, read for its arithmetic only and never stored,
        // so it carries no floors of its own.
        const subtotal = claimQuantity([row], [{ kind: row.kind, globalIds: ids, floorIds: [] }], unitKey);
        return (
          <div key={row.kind} className={styles.typeRow}>
            <div className={styles.typeInfo}>
              <span className={styles.typeName}>{row.kind}</span>
              {measurable ? (
                <span className={styles.typeMeta}>
                  {t('scheduler.panel.stock.chosenOf', { chosen: ids.length, free })}
                  {takenCount > 0 && ` · ${t('scheduler.panel.stock.takenCount', { count: takenCount })}`}
                  {' · '}
                  <button type="button" className={styles.selectLink} onClick={() => onSelect(row.kind)}>
                    {t('scheduler.panel.stock.select')}
                  </button>
                </span>
              ) : (
                <span className={styles.typeMeta}>{t('scheduler.panel.stock.notMeasuredIn', { unit: unitLabel })}</span>
              )}
              {floorIds.length > 1 && (
                <FloorSplit bands={floorBands(row.elements, floorIds, floorNames, floorOf)} chosen={new Set(ids)} />
              )}
            </div>
            <div className={styles.typeSub}>
              {measurable && ids.length > 0 ? (
                <>
                  {`${subtotal} ${unitLabel}`}
                  <small>{t('scheduler.panel.stock.chosenKind', { count: ids.length, kind: row.kind })}</small>
                </>
              ) : (
                <>
                  —
                  {measurable && <small>{t('scheduler.panel.stock.noneChosen')}</small>}
                </>
              )}
            </div>
          </div>
        );
      })}

      <div className={styles.typesTotal}>
        <span>{t('scheduler.panel.stock.totalToOrder')}</span>
        <b>{`${total} ${unitLabel}`}</b>
      </div>
    </div>
  );
}
